<template>
  <b-modal id="CustomConsentFormAiConvertModal"
           size="lg"
           no-close-on-backdrop
           :no-close-on-esc="isConverting"
           @hidden="resetForm">
    <template #modal-header>
      <h5 class="mb-0">
        <font-awesome-icon icon="fa-solid fa-wand-magic-sparkles" class="mr-2" />{{ $t('AiConvertTitle') }}
      </h5>
      <font-awesome-icon v-if="!isConverting" class="close" icon="fa-regular fa-xmark" @click="closeModal()" />
    </template>

    <!-- Dönüştürme sürerken girdi alanları yerine ilerleme paneli gösterilir:
         çağrı 5-20 sn sürüyor, boş bir spinner "takıldı mı?" hissi veriyor. -->
    <div v-if="isConverting" class="text-center py-4">
      <font-awesome-icon icon="fa-solid fa-wand-magic-sparkles" size="2x" class="ai-icon mb-3" />
      <h6>{{ $t('AiConvertConverting') }}</h6>
      <p class="text-muted mb-2">{{ currentStepText }}</p>
      <b-progress :value="progressPercent" :max="100" animated class="mb-2"></b-progress>
      <small class="text-muted">{{ $t('AiConvertConvertingHint') }}</small>
    </div>

    <template v-else>
      <p class="text-muted">{{ $t('AiConvertSubtitle') }}</p>

      <b-button-group class="d-flex mb-3">
        <b-button :variant="mode === 'file' ? 'primary' : 'outline-primary'" @click="mode = 'file'">
          <font-awesome-icon icon="fa-solid fa-file-arrow-up" class="mr-1" />{{ $t('AiConvertModeFile') }}
        </b-button>
        <b-button :variant="mode === 'text' ? 'primary' : 'outline-primary'" @click="mode = 'text'">
          <font-awesome-icon icon="fa-solid fa-paste" class="mr-1" />{{ $t('AiConvertModeText') }}
        </b-button>
        <b-button :variant="mode === 'catalog' ? 'primary' : 'outline-primary'"
                  @click="mode = 'catalog'; loadCatalog()">
          <font-awesome-icon icon="fa-solid fa-list" class="mr-1" />{{ $t('AiConvertModeCatalog') }}
        </b-button>
      </b-button-group>

      <!-- Dosya modu -->
      <div v-if="mode === 'file'">
        <div v-if="!file"
             class="ai-dropzone"
             :class="{ 'is-dragging': isDragging }"
             @drop.prevent="onFileDrop"
             @dragover.prevent="isDragging = true"
             @dragleave.prevent="isDragging = false"
             @click="$refs.fileInput.click()">
          <font-awesome-icon icon="fa-solid fa-cloud-arrow-up" size="2x" class="mb-2" />
          <div>{{ $t('AiConvertDropzoneTitle') }}</div>
          <small class="text-muted d-block">{{ $t('AiConvertDropzoneFormats') }}</small>
          <small class="text-muted">{{ $t('AiConvertDropzoneLimit').replace('{size}', maxFileSizeMb) }}</small>
        </div>

        <div v-else class="ai-selected-file">
          <font-awesome-icon :icon="fileIcon" size="lg" class="mr-2" />
          <div class="flex-grow-1 text-truncate">
            <div class="text-truncate" :title="file.name">{{ file.name }}</div>
            <small class="text-muted">{{ formattedFileSize }}</small>
          </div>
          <b-button size="sm" variant="outline-danger" :title="$t('Remove')" @click="clearFile()">
            <font-awesome-icon icon="fa-regular fa-xmark" />
          </b-button>
        </div>

        <input ref="fileInput"
               type="file"
               :accept="acceptedExtensions"
               style="display: none;"
               @change="onFileSelected">

        <b-alert :show="!!fileError" variant="danger" class="mt-3 mb-0 py-2">
          <font-awesome-icon icon="fa-solid fa-circle-exclamation" class="mr-2" />{{ fileError }}
        </b-alert>

        <small class="text-muted d-block mt-3">
          <font-awesome-icon icon="fa-solid fa-circle-info" class="mr-1" />{{ $t('AiConvertScannedPdfNote') }}
        </small>
      </div>

      <!-- Katalog modu: mevcut onam tanımı okunur, ASLA değiştirilmez -->
      <div v-else-if="mode === 'catalog'">
        <div v-if="catalogLoading" class="text-muted">
          <i class="fa fa-spinner fa-spin mr-2"></i>{{ $t('Loading') }}
        </div>
        <div v-else-if="!catalogList.length" class="text-muted">
          <font-awesome-icon icon="fa-solid fa-circle-info" class="mr-2" />{{ $t('AiConvertCatalogEmpty') }}
        </div>
        <template v-else>
          <label>{{ $t('AiConvertCatalogPickLabel') }}</label>
          <v-select transition=""
                    v-model="consentFormId"
                    :reduce="s => s.id"
                    label="name"
                    :options="catalogList"
                    :placeholder="$t('AiConvertCatalogPickLabel')">
            <template v-slot:no-options> {{ $t('NoMatchingOptionsMessage') }} </template>
          </v-select>
          <small class="text-muted d-block mt-3">
            <font-awesome-icon icon="fa-solid fa-circle-info" class="mr-1" />{{ $t('AiConvertCatalogNote') }}
          </small>
        </template>
      </div>

      <!-- Metin modu -->
      <div v-else>
        <b-form-textarea v-model="text" rows="10" :placeholder="$t('AiConvertTextPlaceholder')"></b-form-textarea>
        <div class="d-flex justify-content-between mt-1">
          <small :class="{ 'text-danger': isTextTooLong }">{{ text.length }} / {{ maxTextLength }}</small>
          <small v-if="isTextTooLong" class="text-danger">
            <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="mr-1" />{{ $t('AiConvertTextTooLong') }}
          </small>
        </div>
      </div>

      <!-- Katalogda kaynak zaten bilinen bir onam; tedavi ipucu nadiren gerekir. -->
      <b-form-group v-if="mode !== 'catalog'" class="mt-4"
                    :label="$t('AiConvertTreatmentHint')"
                    :description="$t('AiConvertTreatmentHintHelp')">
        <b-form-input v-model="hintTreatmentName"
                      :placeholder="$t('AiConvertTreatmentHintPlaceholder')"></b-form-input>
      </b-form-group>

      <b-alert :show="hasExistingData" variant="warning" class="mt-3 mb-0 py-2">
        <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="mr-2" />{{ $t('AiConvertOverwriteWarning') }}
      </b-alert>
    </template>

    <template #modal-footer>
      <small v-if="!isConverting" class="text-muted mr-auto">
        <font-awesome-icon icon="fa-solid fa-shield-halved" class="mr-1" />{{ $t('AiConvertNoSaveNote') }}
      </small>
      <b-button variant="secondary" :disabled="isConverting" @click="closeModal()">
        {{ $t('Cancel') }}
      </b-button>
      <b-button variant="primary" :disabled="isConverting || !canConvert" @click="convert()">
        <span v-if="isConverting">
          <i class="fa fa-spinner fa-spin mr-1"></i>{{ $t('AiConvertConverting') }}
        </span>
        <span v-else>
          <font-awesome-icon icon="fa-solid fa-wand-magic-sparkles" class="mr-1" />{{ $t('AiConvertSubmit') }}
        </span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
  import consentFormService from '../../services/customerConsentForm'

  // Converter tarafındaki limitlerle aynı: erken uyarı sunucuya boşuna gitmemek için.
  const MAX_FILE_SIZE_MB = 15;
  const MAX_TEXT_LENGTH = 60000;
  const ALLOWED_EXTENSIONS = ['pdf', 'docx', 'html', 'htm'];

  export default {
    name: 'CustomConsentFormAiConvertModal',
    props: {
      // Ekranda seçili dil. Doluysa AI'ın dil tahminini ezer.
      culture: {
        type: String,
        default: ''
      },
      // Formda halihazırda veri varsa kullanıcıyı ezme konusunda uyarırız.
      hasExistingData: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        mode: 'file',
        file: null,
        fileError: '',
        text: '',
        hintTreatmentName: '',
        isConverting: false,
        isDragging: false,

        // Katalog modu: tenant'ta açık olan mevcut (legacy) onam tanımları.
        // Yalnız okunur — buradan çevirmek legacy formu etkilemez.
        consentFormId: null,
        catalogList: [],
        catalogLoading: false,
        catalogLoaded: false,

        stepIndex: 0,
        stepTimer: null,

        maxFileSizeMb: MAX_FILE_SIZE_MB,
        maxTextLength: MAX_TEXT_LENGTH,
        acceptedExtensions: '.pdf,.docx,.html,.htm'
      }
    },
    computed: {
      canConvert() {
        if (this.mode === 'file') return !!this.file && !this.fileError;
        if (this.mode === 'catalog') return !!this.consentFormId;

        // Sınır aşıldığında uyarı basıp göndermeye izin vermek istemci kontrolünü
        // anlamsız kılıyordu: converter metni kesip güveni düşürüyor.
        return this.text.trim().length > 0 && !this.isTextTooLong;
      },
      isTextTooLong() {
        return this.text.length > MAX_TEXT_LENGTH;
      },
      formattedFileSize() {
        if (!this.file) return '';

        const kb = this.file.size / 1024;
        return kb < 1024 ? kb.toFixed(0) + ' KB' : (kb / 1024).toFixed(1) + ' MB';
      },
      fileIcon() {
        const extension = this.getExtension(this.file && this.file.name);

        if (extension === 'pdf') return 'fa-solid fa-file-pdf';
        if (extension === 'docx') return 'fa-solid fa-file-word';
        if (extension === 'html' || extension === 'htm') return 'fa-solid fa-file-code';
        return 'fa-solid fa-file-lines';
      },
      // Adımlar sabit süreli değil; gerçek ilerlemeyi bilmediğimiz için beklenen
      // sırayı gösterip ilerleme çubuğunu adıma bağlıyoruz.
      conversionSteps() {
        // Katalogda da bir okuma adımı var (CDN'den gövde çekilir).
        return this.mode === 'text'
          ? ['AiConvertStepExtracting', 'AiConvertStepParsing', 'AiConvertStepMapping', 'AiConvertStepFinalizing']
          : ['AiConvertStepReading', 'AiConvertStepExtracting', 'AiConvertStepParsing', 'AiConvertStepMapping', 'AiConvertStepFinalizing'];
      },
      currentStepText() {
        return this.$t(this.conversionSteps[Math.min(this.stepIndex, this.conversionSteps.length - 1)]);
      },
      progressPercent() {
        // Son adımda %100 gösterip beklemek yanıltıcı olur; %92'de durur.
        return Math.min(92, Math.round(((this.stepIndex + 1) / this.conversionSteps.length) * 92));
      }
    },
    beforeDestroy() {
      this.stopStepTimer();
    },
    methods: {
      open() {
        this.$bvModal.show('CustomConsentFormAiConvertModal');
      },
      closeModal() {
        this.$bvModal.hide('CustomConsentFormAiConvertModal');
      },
      resetForm() {
        this.clearFile();
        this.text = '';
        this.hintTreatmentName = '';
        this.consentFormId = null;
        this.mode = 'file';
      },
      /**
       * Mevcut onam listesi (aynı uç danışana onam atamada da kullanılıyor).
       * Yalnız legacy satırlar alınır: özel formu tekrar özele çevirmek anlamsız.
       * Bir kez yüklenir; modal her açılışta yeniden istek atmaz.
       */
      loadCatalog() {
        if (this.catalogLoaded || this.catalogLoading) return;

        this.catalogLoading = true;

        consentFormService.getConsentFormList()
          .then(result => {
            this.catalogList = (result || [])
              .filter(item => !item.isCustom && item.reportName)
              .map(item => ({ id: item.id, name: item.name }));
            this.catalogLoaded = true;
          })
          .catch(() => {
            // Liste gelmezse mod boş görünür; dosya/metin yolları çalışmaya devam eder.
            this.catalogList = [];
          })
          .finally(() => { this.catalogLoading = false; });
      },
      clearFile() {
        this.file = null;
        this.fileError = '';
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }
      },
      getExtension(fileName) {
        if (!fileName) return '';

        const parts = fileName.split('.');
        return parts.length > 1 ? parts.pop().toLowerCase() : '';
      },
      onFileSelected(event) {
        this.setFile(event.target.files.length > 0 ? event.target.files[0] : null);
      },
      onFileDrop(event) {
        this.isDragging = false;

        const files = event.dataTransfer && event.dataTransfer.files;
        if (files && files.length > 0) {
          this.setFile(files[0]);
        }
      },
      setFile(file) {
        this.fileError = '';
        this.file = file;

        if (!file) return;

        const extension = this.getExtension(file.name);

        // .doc converter tarafında da reddediliyor; kullanıcıyı bekletmeden söylüyoruz.
        if (extension === 'doc') {
          this.fileError = this.$t('AiConvertLegacyDocError');
          return;
        }

        if (!ALLOWED_EXTENSIONS.includes(extension)) {
          this.fileError = this.$t('AiConvertUnsupportedFileError');
          return;
        }

        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          this.fileError = this.$t('AiConvertFileTooLargeError').replace('{size}', MAX_FILE_SIZE_MB);
        }
      },
      startStepTimer() {
        this.stepIndex = 0;
        this.stopStepTimer();
        this.stepTimer = setInterval(() => {
          if (this.stepIndex < this.conversionSteps.length - 1) {
            this.stepIndex++;
          }
        }, 2600);
      },
      stopStepTimer() {
        if (this.stepTimer) {
          clearInterval(this.stepTimer);
          this.stepTimer = null;
        }
      },
      convert() {
        if (!this.canConvert) return;

        let form = new FormData();

        if (this.mode === 'file') {
          form.append('file', this.file);
        } else if (this.mode === 'catalog') {
          form.append('consentFormId', this.consentFormId);
        } else {
          form.append('text', this.text);
        }

        if (this.culture) {
          form.append('culture', this.culture);
        }

        if (this.mode !== 'catalog' && this.hintTreatmentName) {
          form.append('hintTreatmentName', this.hintTreatmentName);
        }

        this.isConverting = true;
        this.startStepTimer();

        consentFormService.convertFromDocument(form)
          .then(result => {
            this.$emit('converted', result);
            this.closeModal();
          })
          .catch(error => {
            this.showError(error);
          })
          .finally(() => {
            this.isConverting = false;
            this.stopStepTimer();
          });
      },
      showError(error) {
        const data = error && error.response && error.response.data;

        // 422'de converter'ın kullanıcıya gösterilebilir açıklaması gelir
        // (taranmış PDF, .doc vb.); yoksa çeviri anahtarına düşülür.
        if (data && data.detail) {
          this.$toastr.error(data.detail);
          return;
        }

        if (data && data.messageKey) {
          this.$toastr.error(this.$t(data.messageKey));
          return;
        }

        this.$toastr.error(this.$t('AiConvertFailed'));
      }
    }
  }
</script>

<style scoped>
  .ai-icon {
    color: var(--iq-primary);
  }

  .ai-dropzone {
    border: 2px dashed #d7dbe0;
    border-radius: 6px;
    padding: 32px 16px;
    text-align: center;
    cursor: pointer;
    color: #6c757d;
  }

  .ai-dropzone.is-dragging,
  .ai-dropzone:hover {
    border-color: var(--iq-primary);
    background: #f7f8fa;
  }

  .ai-selected-file {
    display: flex;
    align-items: center;
    border: 1px solid #e3e6ea;
    border-radius: 6px;
    padding: 12px;
  }
</style>
